import React from 'react';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Legend } from 'recharts';
import { SortationData } from './types';
import { dashboardColors } from './colors';
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  useBreakpointValue,
  Flex,
} from '@chakra-ui/react';
import { formatNumber, formatPercentageNumber } from './utils';

interface SortasiPKSProps {
  data: SortationData[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <Box 
        bg={bgColor} 
        p={3} 
        borderWidth={1} 
        borderColor={borderColor} 
        borderRadius="md" 
        shadow="md"
      >
        <Box fontWeight="semibold">{`Tanggal: ${label}`}</Box>
        <Box fontSize="sm">
          {/* {`Total Sortasi: ${formatPercentageNumber(data.total_deduction_prc, 2)}%`} */}
        </Box>
        <Box fontSize="sm">
          {`ABU: ${formatNumber(data.ABU)} kg (${formatPercentageNumber(data.ABU_prc, 2)}%)`}
        </Box>
        <Box fontSize="sm">
          {`LKK: ${formatNumber(data.LKK)} kg (${formatPercentageNumber(data.LKK_prc, 2)}%)`}
        </Box>
        <Box fontSize="sm">
          {`VIN: ${formatNumber(data.VIN)} kg (${formatPercentageNumber(data.VIN_prc, 2)}%)`}
        </Box>
        <Box fontSize="sm">
          {`IAM: ${formatNumber(data.IAM)} kg (${formatPercentageNumber(data.IAM_prc, 2)}%)`}
        </Box>
        <Box fontSize="sm">
          {`Total: ${formatNumber(data.total_deduction_weight)} kg (${formatPercentageNumber(data.total_deduction_prc, 2)}%)`}
        </Box>
      </Box>
    );
  }
  return null;
};

const SortasiPKS: React.FC<SortasiPKSProps> = ({ data }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  // Group data by date and supplier
  const groupedData = data.reduce((acc, item) => {
    const dateKey = item.date_posting;
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: formatDate(item.date_posting),
        fullDate: item.date_posting,
        ABU: 0,
        LKK: 0,
        VIN: 0,
        IAM: 0,
        ABU_prc: 0,
        LKK_prc: 0,
        VIN_prc: 0,
        IAM_prc: 0,
        ABU_net: 0,
        LKK_net: 0,
        VIN_net: 0,
        IAM_net: 0,
        total_deduction_prc: 0,
        total_deduction_weight: 0,
        total_net_weight: 0
      };
    }
    
    // Add supplier data
    const supplierKey = item.supplier as 'ABU' | 'LKK' | 'VIN' | 'IAM';
    acc[dateKey][supplierKey] = item.deduction_weight;
    acc[dateKey][`${supplierKey}_net`] = item.net_weight;
    acc[dateKey][`${supplierKey}_prc`] = item.deduction_prc;
    
    acc[dateKey].total_deduction_weight += item.deduction_weight;
    acc[dateKey].total_net_weight += item.net_weight;
    
    return acc;
  }, {} as Record<string, any>);

  // Convert to array and calculate total percentage
  const chartData = Object.values(groupedData).map((item: any) => ({
    ...item,
    total_deduction_prc: item.total_net_weight > 0 ? (item.total_deduction_weight / item.total_net_weight) * 100 : 0
  })).sort((a: any, b: any) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());

  // Calculate statistics
  const totalDeductionWeight = data.reduce((sum, item) => sum + item.deduction_weight, 0);
  const totalNetWeight = data.reduce((sum, item) => sum + item.net_weight, 0);
  const avgSortasi = totalNetWeight > 0 ? (totalDeductionWeight / totalNetWeight) * 100 : 0;

  // Define supplier colors
  const supplierColors = {
    ABU: '#22C55E', // Green
    LKK: '#7C3AED', // Purple  
    VIN: '#3B82F6',  // Blue
    IAM: '#F59E0B'  // Yellow
  };

  const rowHeight = useBreakpointValue({ 
      base: '110%',     // mobile - smaller
      md: '110%',       // tablet
      lg: '100%',       // laptop
      xl: '100%',      // desktop
      '2xl': '110%'    // large desktop
  });

  const chartMinHeight = useBreakpointValue({
    base: '380px', // mobile
    md: '380px',   // tablet
    lg: '325px',   // laptop
    xl: '275px',   // desktop
    '2xl': '330px' // large desktop
  }) || '300px';

  return (
    <Box bg={useColorModeValue('white', 'gray.700')} borderRadius="lg" p={4} h="100%" >
      <Heading as="h3" size="md" mb={2} color={useColorModeValue('gray.800', 'white')} fontSize="18px">
        SORTASI PKS
      </Heading>
      
      {/* Summary Cards */}
      {/* <SimpleGrid columns={2} spacing={4} mb={4}>
        <Box bg={useColorModeValue('red.50', 'red.900')} borderWidth={1} borderColor={useColorModeValue('red.200', 'red.700')} borderRadius="lg" p={3}>
          <Text color="red.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Rata-rata Sortasi
          </Text>
          <Text color={useColorModeValue('red.900', 'red.200')} fontSize="xl" fontWeight="bold">
            {avgSortasi.toFixed(2)}%
          </Text>
          <Text color="red.600" fontSize="xs">Persentase</Text>
        </Box>
        
        <Box bg={useColorModeValue('yellow.50', 'yellow.900')} borderWidth={1} borderColor={useColorModeValue('yellow.200', 'yellow.700')} borderRadius="lg" p={3}>
          <Text color="yellow.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Total Sortasi
          </Text>
          <Text color={useColorModeValue('yellow.900', 'yellow.200')} fontSize="xl" fontWeight="bold">
            {totalSortasi.toLocaleString()}
          </Text>
          <Text color="yellow.600" fontSize="xs">kg</Text>
        </Box>
      </SimpleGrid> */}
      <Flex align="center" gap={6} mb={0} mt={0} justify="center">
        <Text fontSize="sm" color={dashboardColors.error} fontWeight="semibold">
          Rata-rata Sortasi: <Box as="span" fontWeight="bold" color={dashboardColors.error} display="inline">{avgSortasi ? `${formatPercentageNumber(avgSortasi, 2)}%` : '-'}</Box>
        </Text>
      </Flex>

      {/* Legend for suppliers */}
      {/* <Flex align="center" gap={4} mb={2} justify="center" flexWrap="wrap">
        <Flex align="center" gap={2}>
          <Box w={3} h={3} bg={supplierColors.ABU} borderRadius="sm" />
          <Text fontSize="xs" fontWeight="medium">ABU</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Box w={3} h={3} bg={supplierColors.LKK} borderRadius="sm" />
          <Text fontSize="xs" fontWeight="medium">LKK</Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Box w={3} h={3} bg={supplierColors.VIN} borderRadius="sm" />
          <Text fontSize="xs" fontWeight="medium">VIN</Text>
        </Flex>
      </Flex> */}

      {/* Chart */}
      {/* <Box minH="0" height="full"> */}
      <Box minH={chartMinHeight} h={chartMinHeight}>
        <ResponsiveContainer width="100%" height={rowHeight}>
          <ComposedChart
            data={chartData}
            // margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            margin={{ top: 10, right: 0, left: 10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date"
              fontSize={10}
              // angle={-45}
              // textAnchor="end"
              height={60}
            />
            <YAxis
              dataKey="total_deduction_weight"
              fontSize={12}
              tickFormatter={formatNumber}
              label={{
                value: 'Berat (kg)',
                angle: -90,
                position: 'insideLeft',
                offset: -5,
                fontSize: 14,
                // fill: '#666',
              }}
            />
            {/* y axis deduction_prc */}
            <YAxis 
              yAxisId="right"
              orientation="right"
              dataKey="total_deduction_prc"
              fontSize={12}
              tickFormatter={(value: number) => `${value.toFixed(0)}%`}
              label={{
                value: 'Sortasi',
                angle: 90,
                position: 'insideRight',
                offset: 8,
                fontSize: 14,
                align: 'center',
                fill: '#666',
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Stacked bars for each supplier */}
            <Bar
              dataKey="ABU" 
              stackId="suppliers"
              fill={supplierColors.ABU}
              name="ABU"
              barSize={18}
            />
            <Bar
              dataKey="LKK" 
              stackId="suppliers"
              fill={supplierColors.LKK}
              name="LKK"
              barSize={18}
            />
            <Bar
              dataKey="VIN" 
              stackId="suppliers"
              fill={supplierColors.VIN}
              name="VIN"
              barSize={18}
            />
            <Bar
              dataKey="IAM" 
              stackId="suppliers"
              fill={supplierColors.IAM}
              name="IAM"
              barSize={18}
            />
            {/* Line chart deduction_prc */}
            <Line
              yAxisId="right"
              dataKey="total_deduction_prc"
              stroke={dashboardColors.error}
              name="Total Sortasi"
              strokeWidth={3}
              dot={{ r: 5 }}
              activeDot={{ r: 6 }}
            />
            <Legend
                verticalAlign="bottom"
                align="center"
                height={30}
                wrapperStyle={{ fontSize: 12, bottom: 35 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Statistics */}
      {/* <SimpleGrid columns={3} spacing={4} mt={4}>
        <Box bg={useColorModeValue('gray.50', 'gray.800')} borderWidth={1} borderColor={useColorModeValue('gray.200', 'gray.700')} borderRadius="lg" p={3}>
          <Text color="gray.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Tertinggi
          </Text>
          <Text color={useColorModeValue('gray.900', 'gray.100')} fontSize="lg" fontWeight="bold">
            {maxSortasi.toFixed(2)}%
          </Text>
          <Text color="gray.600" fontSize="xs">Sortasi</Text>
        </Box>
        
        <Box bg={useColorModeValue('gray.50', 'gray.800')} borderWidth={1} borderColor={useColorModeValue('gray.200', 'gray.700')} borderRadius="lg" p={3}>
          <Text color="gray.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Terendah
          </Text>
          <Text color={useColorModeValue('gray.900', 'gray.100')} fontSize="lg" fontWeight="bold">
            {minSortasi.toFixed(2)}%
          </Text>
          <Text color="gray.600" fontSize="xs">Sortasi</Text>
        </Box>
        
        <Box bg={useColorModeValue('gray.50', 'gray.800')} borderWidth={1} borderColor={useColorModeValue('gray.200', 'gray.700')} borderRadius="lg" p={3}>
          <Text color="gray.600" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
            Total Hari
          </Text>
          <Text color={useColorModeValue('gray.900', 'gray.100')} fontSize="lg" fontWeight="bold">
            {data.length}
          </Text>
          <Text color="gray.600" fontSize="xs">Hari</Text>
        </Box>
      </SimpleGrid> */}
    </Box>
  );
};

export default SortasiPKS;
